'use client';

import type { CSSProperties } from 'react';
import { useTranslations } from 'next-intl';
import CustomLink from 'components/shared/CustomLink';
import { useMarketingBannerOffset } from 'components/shared/Header/MarketingBanner';
import { getSpeedsizeImageUrl } from 'components/shared/custom-image/CustomImage';
import { campaignHeroCtaProps } from './v3/homepageCta';
import v3 from './v3/v3.module.css';
import styles from './TwoHourDeliveryHero.module.css';

const ART_BASE = 'web_v2/homepage-v3/banners';

type HeroArt = {
  desktop: string;
  desktopSrcSet?: string;
  mobile: string;
  mobileSrcSet?: string;
};

const DEFAULT_ART: HeroArt = {
  desktop: getSpeedsizeImageUrl(
    `${ART_BASE}/two-hour-delivery/desktop/two-hr-banner.png`,
    1920,
  ),
  mobile: getSpeedsizeImageUrl(
    `${ART_BASE}/two-hour-delivery/mobile/two-hour-banner.png`,
    800,
  ),
};

// Figma UH-Landing-Page 1877:10050 (2x, 3160×1120) / 1877:10137 (3x,
// 1206×2622). The card is ~1420 CSS px on desktop and ~400 on phones, so
// retina needs the 2880 / 1200 variants; 1920 / 800 alone upscale and blur.
const CAMPAIGN_DESKTOP = `${ART_BASE}/desktop/banner-8.png`;
const CAMPAIGN_MOBILE = `${ART_BASE}/mobile/banner-7.jpg`;
const CAMPAIGN_ART: HeroArt = {
  desktop: getSpeedsizeImageUrl(CAMPAIGN_DESKTOP, 1920),
  desktopSrcSet: [1920, 2880]
    .map((w) => `${getSpeedsizeImageUrl(CAMPAIGN_DESKTOP, w)} ${w}w`)
    .join(', '),
  mobile: getSpeedsizeImageUrl(CAMPAIGN_MOBILE, 800),
  mobileSrcSet: [800, 1200]
    .map((w) => `${getSpeedsizeImageUrl(CAMPAIGN_MOBILE, w)} ${w}w`)
    .join(', '),
};

export type TwoHourHeroCampaign = {
  /** Percentage off Ring AIR, from the live ring campaign. */
  discountPercent: number;
};

type TwoHourDeliveryHeroProps = {
  /** True when HeroCarousel already owns header clearance above this card. */
  stackedBelowCarousel?: boolean;
  /** Which homepage.* keys and testid to use. Defaults to the IN 2-hour copy. */
  variant?: 'twoHour' | 'fourHourAe';
  /**
   * Set while a percentage ring campaign runs. IN swaps in the campaign photo
   * and re-skin. AE keeps its own photo and colors and only uses the
   * "{n}% off / delivered in 4 hours" headline.
   */
  campaign?: TwoHourHeroCampaign;
};

// Full literal keys (not a template-built prefix), same pattern as
// PerformanceLabTile's desktopKey/mobileKey props: keeps every key statically
// grep-able by the i18n prune/key-shape checks instead of pinning a whole
// dynamic subtree.
const VARIANT_CONFIG = {
  twoHour: {
    testId: 'two-hour-delivery-hero',
    ariaSection: 'homepage.twoHourHero.aria.section',
    eyebrow: 'homepage.twoHourHero.eyebrow',
    titleLine1: 'homepage.twoHourHero.titleLine1',
    titleLine2: 'homepage.twoHourHero.titleLine2',
    subtitle: 'homepage.twoHourHero.subtitle',
    campaignAria: 'homepage.twoHourHero.campaign.aria.section',
    campaignTitleLine1: 'homepage.twoHourHero.campaign.titleLine1',
    campaignTitleLine2: 'homepage.twoHourHero.campaign.titleLine2',
    campaignSubtitle: 'homepage.twoHourHero.campaign.subtitle',
  },
  fourHourAe: {
    testId: 'four-hour-delivery-hero',
    ariaSection: 'homepage.fourHourHero.aria.section',
    eyebrow: 'homepage.fourHourHero.eyebrow',
    titleLine1: 'homepage.fourHourHero.titleLine1',
    titleLine2: 'homepage.fourHourHero.titleLine2',
    subtitle: 'homepage.fourHourHero.subtitle',
    campaignAria: 'homepage.fourHourHero.campaign.aria.section',
    campaignTitleLine1: 'homepage.fourHourHero.campaign.titleLine1',
    campaignTitleLine2: 'homepage.fourHourHero.campaign.titleLine2',
  },
} as const;

export const TwoHourDeliveryHero = ({
  stackedBelowCarousel = false,
  variant = 'twoHour',
  campaign,
}: TwoHourDeliveryHeroProps) => {
  const t = useTranslations('homepage');
  const bannerPx = useMarketingBannerOffset();
  const firstFold = !stackedBelowCarousel;
  const keys = VARIANT_CONFIG[variant];
  // Discount headline for either region. The dark campaign photo is IN only.
  const discountCopy = campaign;
  const campaignSkin = variant === 'twoHour' ? campaign : undefined;
  const art = campaignSkin ? CAMPAIGN_ART : DEFAULT_ART;

  return (
    <section
      className={`${styles.section} ${
        stackedBelowCarousel ? styles.stacked : ''
      }`}
      aria-label={discountCopy ? t(keys.campaignAria) : t(keys.ariaSection)}
      data-testid={keys.testId}
      data-variant={campaignSkin ? 'campaign' : undefined}
      data-header-theme="light"
      style={
        {
          '--campaign-banner-px': `${bannerPx}px`,
          '--frost-photo': `url("${art.mobile}")`,
        } as CSSProperties
      }
    >
      <picture>
        <source
          media="(max-width: 899px)"
          srcSet={art.mobileSrcSet ?? art.mobile}
          sizes={art.mobileSrcSet ? '100vw' : undefined}
        />
        <img
          src={art.desktop}
          srcSet={art.desktopSrcSet}
          sizes={art.desktopSrcSet ? '100vw' : undefined}
          alt=""
          className={styles.bg}
          fetchPriority={firstFold ? 'high' : undefined}
          loading={firstFold ? undefined : 'lazy'}
          decoding="async"
        />
      </picture>
      <div className={styles.overlay} aria-hidden />
      <div className={styles.bottomScrim} aria-hidden />
      <div className={styles.copy}>
        <div className={styles.titleGroup}>
          <p className={styles.eyebrow}>{t(keys.eyebrow)}</p>
          <h2 className={`${v3.campaignDisplay} ${styles.title}`}>
            {discountCopy
              ? t(keys.campaignTitleLine1, {
                  discount: discountCopy.discountPercent,
                })
              : t(keys.titleLine1)}
            <br />
            {discountCopy ? t(keys.campaignTitleLine2) : t(keys.titleLine2)}
          </h2>
          <p className={styles.subtitle}>
            {variant === 'twoHour' && discountCopy
              ? t(VARIANT_CONFIG.twoHour.campaignSubtitle)
              : t(keys.subtitle)}
          </p>
        </div>
        <CustomLink
          href="/ring/buy/"
          className={`${v3.pill} ${styles.cta}`}
          {...campaignHeroCtaProps('ring')}
        >
          {t('homepage.cta.buyNow')}
        </CustomLink>
      </div>
    </section>
  );
};
